import React, {useMemo, useState} from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { plusIcon, pencilIcon, trashIcon } from '@progress/kendo-svg-icons';
import styles from './CustomerDetailsLinks.module.scss';
import stylesLink from './CustomerDetailsLink.module.scss';
import CustomerDetailsLink from './CustomerDetailsLink';
import defIcon from '../../../../assets/link_icon.svg';
import gridViewIcon from '../../../../assets/gridViewIcon.svg';
import gridViewIconActive from '../../../../assets/gridViewIconActive.svg';
import tilesViewIcon from '../../../../assets/tilesViewIcon.svg';
import tilesViewIconActive from '../../../../assets/tilesViewIconActive.svg';
import { ICustomerLink } from '../../../../model';
import CommonModal from '../../../common/modal/CommonModal';
import {usePermissions} from "../../../auth/PermissionsProvider";

export type ViewMode = 'tiles' | 'grid';
interface Props {
  edit: boolean;
  formRenderProps: any;
  isNew?: boolean | undefined;
}

export const NewCustomerLink = (): ICustomerLink => ({
  id: 0,
  url: null,
  title: null,
  description: '',
});

const CustomerDetailsLinks = (props: Props) => {
  const { formRenderProps, edit, isNew } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState<number>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentLink, setCurrentLink] = useState<ICustomerLink | null>(null);
  const { hasEditAccess } = usePermissions();
  let customerLinks = formRenderProps.valueGetter('customerLinks');

  const changeViewMode = () => {
    if (viewMode === 'tiles') {
      setViewMode('grid');
    } else {
      setViewMode('tiles');
    }
  };

  const addLink = () => {
    const currentLinks = formRenderProps.valueGetter('customerLinks') || [];
    const newLinkList = [...currentLinks, NewCustomerLink()];
    const newLinkIndex = newLinkList.length - 1;

    formRenderProps.onChange('customerLinks', { value: newLinkList });

    setEditingLinkIndex(newLinkIndex);
    setCurrentLink(newLinkList[newLinkIndex]);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const editLink = (index: number) => {
    setEditingLinkIndex(index);
    setCurrentLink(customerLinks[index]);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const removeLink = (indexToRemove: number) => {
    const currentLinks: ICustomerLink[] = formRenderProps.valueGetter('customerLinks') || [];
    const updatedLinks = currentLinks.filter((_, index) => index !== indexToRemove);

    formRenderProps.onChange('customerLinks', { value: updatedLinks });

    setTimeout(() => {
      if (formRenderProps && formRenderProps.onSubmit) {
        formRenderProps.onSubmit();
      }
    }, 0);
  };

  const handleSaveChanges = () => {
    setIsModalOpen(false);
    if (isNew) {
      const preCustomerLinks = formRenderProps.valueGetter('customerLinks');
      formRenderProps.onChange('customerLinks', { value: preCustomerLinks });
      customerLinks = [...preCustomerLinks];
    }

    if (!isNew && formRenderProps && formRenderProps.onSubmit) {
      formRenderProps.onSubmit();
    }
  };

  const handleCloseModal = () => {
    if (!isEditMode) {
      const currentLinks = formRenderProps.valueGetter('customerLinks') || [];
      const updatedLinks = currentLinks.slice(0, -1);
      formRenderProps.onChange('customerLinks', { value: updatedLinks });
    } else {
      if (editingLinkIndex !== undefined && currentLink) {

        const currentLinks = formRenderProps.valueGetter('customerLinks') || [];

        const updatedLinks = [...currentLinks];

        updatedLinks[editingLinkIndex] = currentLink;

        formRenderProps.onChange('customerLinks', { value: updatedLinks });
      }
    }
    setIsModalOpen(false);
  };

  const isLinksModalValid = useMemo(() => {
    if (editingLinkIndex === undefined) {
      return false;
    }

    const errors = formRenderProps.errors;

    const allLinks = formRenderProps.valueGetter('customerLinks') || [];
    const currentLink = allLinks[editingLinkIndex];

    if (!currentLink) return false;

    const fieldPrefix = `customerLinks[${editingLinkIndex}]`;
    const hasErrors = Object.keys(errors).some((key) => key.startsWith(fieldPrefix) && errors[key]);

    const requiredFieldsFilled = !!currentLink.url && !!currentLink.title;

    return requiredFieldsFilled && !hasErrors;
  }, [formRenderProps, editingLinkIndex]);

  return (
      <>
        <div className={styles.headerBlock}>
          {hasEditAccess && <Button svgIcon={plusIcon} onClick={() => addLink()} type='button'>
            Add Link
          </Button>}


          <button type='button' onClick={changeViewMode} className={styles.iconButton} aria-label='Toggle view mode'>
            <img className={styles.tilesView} src={viewMode === 'tiles' ? tilesViewIconActive : tilesViewIcon} alt='tiles' />
          </button>

          <button type='button' onClick={changeViewMode} className={styles.iconButton} aria-label='Toggle view mode'>
            <img className={styles.gridView} src={viewMode === 'grid' ? gridViewIconActive : gridViewIcon} alt='grid' />
          </button>
        </div>

        {viewMode === 'tiles' && (
            <div style={{ gap: 20, display: 'flex' }}>
              {customerLinks.map((link: ICustomerLink, index: number) => (
                  <div className={stylesLink.link} key={index}>
                    <a href={link.url ? link.url : ''} target='_blank' rel='noreferrer'>
                      <img src={defIcon} alt={link.description} />
                      <div className={stylesLink.delimiter}></div>
                      <div className={stylesLink.titleDesc}>
                        <span className={stylesLink.title}>{link.title}</span>
                        <span className={stylesLink.desc}>{link.description}</span>
                      </div>
                    </a>
                  </div>
              ))}
            </div>
        )}

        {viewMode === 'grid' && (
            <table style={{ padding: 0 }} className={'table-list'}>
              <thead>
              <tr>
                <th>Name</th>
                <th>URL</th>
                <th>Description</th>
                {hasEditAccess && <th>Actions</th>}
              </tr>
              </thead>
              <tbody>
              {customerLinks.map((link: ICustomerLink, index: number) => (
                  <tr key={index}>
                    <td>{link.title}</td>
                    <td className={styles.shortLink}>
                      <a href={link.url ? link.url : ''} target='_blank' rel='noopener noreferrer'>
                        {link.url}
                      </a>
                    </td>
                    <td className={styles.linkDescription}>
                        {link.description ? link.description : ''}
                    </td>
                    <td>
                      {hasEditAccess && <div className={'td-left'}>
                        <Button type={'button'} onClick={() => editLink(index)}
                                svgIcon={pencilIcon}/>
                        <Button type={'button'} onClick={() => removeLink(index)}
                                svgIcon={trashIcon}/>
                      </div>
                      }

                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
        )}

        <div role={"row"} className={styles.noContactsMessage}>
          {customerLinks?.length === 0 && <span>There are no items to display.</span>}
          {customerLinks?.length === 0 && hasEditAccess &&

              <>
                {" "} Click {" "}
                <button
                    type="button"
                    onClick={() => addLink()}
                    className={styles.linkText}
                >
                  here
                </button>{" "} to add link.
              </>
          }
        </div>


        {isModalOpen && currentLink && (
            <CommonModal
                isOpen={isModalOpen}
                onClose={() => handleCloseModal()}
                title={isEditMode ? `Edit Link: ${currentLink?.title}` : 'Add New Link'}
            >
              <CustomerDetailsLink index={editingLinkIndex ? editingLinkIndex : 0} edit={true} customerLink={currentLink} />
              <div className={styles.modalFooter}>
                <Button type='button' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                    type='button'
                    form='main-customer-form'
                    themeColor={'primary'}
                    disabled={!isLinksModalValid}
                    onClick={handleSaveChanges}
                >
                  Save
                </Button>
              </div>
            </CommonModal>
        )}
      </>
  );
};

export default CustomerDetailsLinks;
